import React, { useState } from 'react';
import { FileText, Settings, ChevronRight, FileEdit, FileCheck, Award } from 'lucide-react';
import { BookingCsvPage } from './BookingCsvPage';
import { LetterheadCreatePage } from './LetterheadCreatePage';
import { OfferLetterCreatePage } from './OfferLetterCreatePage';
import { ExperienceLetterCreatePage } from './ExperienceLetterCreatePage';

type AdminWorkSection = 'overview' | 'booking-csv' | 'letterhead-create' | 'offer-letter-create' | 'experience-letter-create';

export const AdminWorkPage: React.FC = () => {
  const [currentSection, setCurrentSection] = useState<AdminWorkSection>('overview');

  const sections = [
    {
      id: 'booking-csv' as const,
      title: 'Booking CSV',
      description: 'Export booking data for a specific month',
      icon: <FileText className="h-5 w-5" />,
    },
    {
      id: 'letterhead-create' as const,
      title: 'Create Letterhead',
      description: 'Generate professional letterhead documents',
      icon: <FileEdit className="h-5 w-5" />,
    },
    {
      id: 'offer-letter-create' as const,
      title: 'Create Offer Letter',
      description: 'Trip Captain Intern — Offer Letter Generator',
      icon: <FileCheck className="h-5 w-5" />,
    },
    {
      id: 'experience-letter-create' as const,
      title: 'Create Experience Letter',
      description: 'Generate Experience-cum-Relieving Letter',
      icon: <Award className="h-5 w-5" />,
    },
  ];

  if (currentSection === 'booking-csv') {
    return <BookingCsvPage />;
  }

  if (currentSection === 'letterhead-create') {
    return <LetterheadCreatePage />;
  }

  if (currentSection === 'offer-letter-create') {
    return <OfferLetterCreatePage />;
  }

  if (currentSection === 'experience-letter-create') {
    return <ExperienceLetterCreatePage />;
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Settings className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Admin Work</h1>
            <p className="text-sm text-gray-500">Administrative tools and utilities</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setCurrentSection(section.id)}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:border-primary-300 hover:shadow-md transition-all text-left group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-primary-50 rounded-lg text-primary-600 group-hover:bg-primary-100 transition-colors">
                    {section.icon}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {section.title}
                </h3>
                <p className="text-sm text-gray-500">
                  {section.description}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

